import React from "react";
import { DatePicker } from "@/components/common/DatePicker";
import type {
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { NonEditableFormField } from "../../guests/components/form/FormFields";
import type { ReservationUpdateFormData } from "../../guests/utils/reservationSchema";
import type { ReservationDetailsFormData } from "../../utils/mapReservationToFormData";
import RoomField from "../../rooms/components/form/RoomField";
import type { ReservationStatus as ReservationStatusType } from "@/types/reservation";
import ReservationStatusSelect from "../form/ReservationStatusSelect";
import ReservationTimeline from "../ReservationTimeline";
import { PreCheckStatus } from "../../../settlement/types/settlementBooking";
import PreCheckStatusSelect from "../form/PrecheckinStatusSelect";
import ReservationCheckinMethodSelect from "../form/ReservationCheckinMethod";
import { useTranslation } from "react-i18next";
import ISO6391 from "iso-639-1";
import type { GuestStatus } from "@/types/guest";
import { ReservationStatus } from "./ReservationStatus";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CustomQuestionAnswers } from "./CustomQuestionAnswers";

type ReservationInfoProps = {
  reservation?: ReservationDetailsFormData;
  onDateChange: (type: "arrival" | "departure", date: Date) => void;
  guestsCount: { adults: number; children: number };
  guestStatus: GuestStatus | string;
  nights: number;
  roomServiceStatus: string;
  showRoomServiceStatus?: boolean;
  isEditing: boolean;
  watch: UseFormWatch<ReservationUpdateFormData>;
  setValue: UseFormSetValue<ReservationUpdateFormData>;
  errors: FieldErrors<ReservationUpdateFormData>;
};

const getLanguageDisplayName = (code: string) =>
  ISO6391.getNativeName(code) || ISO6391.getName(code);

export const ReservationInfo: React.FC<ReservationInfoProps> = ({
  reservation,
  watch,
  isEditing,
  onDateChange,
  guestsCount,
  setValue,
  errors,
  guestStatus,
  nights,
  roomServiceStatus,
  showRoomServiceStatus = true,
}) => {
  const { t } = useTranslation();

  const arrivalDate = watch("arrivalDate");
  const departureDate = watch("departureDate");
  const roomId = watch("roomId");
  const bookingNumber = watch("bookingNumber");
  const bookingSource = watch("bookingSource");
  const status = watch("status");
  const roomType = watch("roomType");
  const precheckinStatus = watch("preCheckinStatus");
  const preCheckinEnteredGuests = watch("preCheckinEnteredGuests");
  const preCheckinEnteredGuestsReadLabel =
    precheckinStatus === PreCheckStatus.DONE
      ? t("reservation.form.preCheckinEnteredGuestsYes")
      : t("reservation.form.preCheckinEnteredGuestsNo");
  const preCheckinEnteredGuestsSelectValue =
    Number(preCheckinEnteredGuests) === 1 ? "1" : "0";
  const checkinMethod = watch("checkinMethod");
  const estimatedTimeOfArrival = watch("estimatedTimeOfArrival");

  return (
    <>
      <ReservationStatus
        checkinStatus={reservation?.checkinStatus}
        checkoutStatus={reservation?.checkoutStatus}
      />

      <div className="mt-6 flex flex-col gap-4">
        <h1 className="text-violet text-sm">
          {t("reservation.profile.reservationInfo")}
        </h1>

        <ReservationTimeline
          departureDate={departureDate}
          arrivalDate={arrivalDate}
          bookingSource={bookingSource || ""}
          checkinStatus={reservation?.checkinStatus}
          checkoutStatus={reservation?.checkoutStatus}
        />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 md:grid-cols-5">
        <NonEditableFormField
          value={reservation?.id ?? ""}
          isCopy={true}
          label={t("reservation.form.reservationID")}
          labelClassName="text-foreground"
          containerClassName="min-w-0"
          contentClassName="h-9 mt-3 min-w-0 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
          valueClassName="min-w-0 flex-1 overflow-x-auto whitespace-nowrap text-clip"
          buttonClassName="ml-2 shrink-0 !p-0"
        />

        <div className="flex items-start gap-3">
          <span className="mt-0.5 hidden h-5 border-l border-neutral-200 md:block" />
          <div className="flex-1">
            <label className="text-foreground text-sm font-medium">
              {t("reservation.form.arrival")}
            </label>
            <DatePicker
              maxDate={new Date(departureDate)}
              isEdit={isEditing}
              date={new Date(arrivalDate)}
              onDateChange={(date) => onDateChange("arrival", date)}
              placeholder={t("reservation.placeholders.chooseDate")}
              className={`text-muted-foreground mt-3 h-9 min-h-9 w-full justify-start rounded-lg border px-3 py-1 text-sm focus:ring-0 ${errors.arrivalDate ? "border-red-500" : "border-neutral-200"}`}
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              formatString="dd.MM.yyyy"
              showIcon={false}
            />
          </div>
        </div>

        <div className="flex items-start gap-3">
          <span className="mt-0.5 hidden h-5 shrink-0 border-l border-neutral-200 md:block" />
          <NonEditableFormField
            value={guestsCount.adults}
            label={t("reservation.form.numberOfAdults")}
            isCopy={false}
            labelClassName="text-foreground"
            contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
            containerClassName="flex-1"
          />
        </div>

        <div className="flex items-start gap-3">
          <span className="mt-0.5 hidden h-5 border-l border-neutral-200 md:block" />
          <RoomField
            showIcon={false}
            value={roomId}
            onChange={(value) => setValue("roomId", value)}
            error={errors.roomId?.message}
            isEdit={isEditing}
            showOnlyAvailable={true}
            startDate={new Date(arrivalDate)}
            endDate={new Date(departureDate)}
            showCurrentRoom={true}
            currentRoomId={roomId}
            labelClassName="mb-0 text-foreground"
            contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm focus:border-neutral-200 hover:border-neutral-200 data-[size=default]:h-9"
            containerClassName="flex-1"
          />
        </div>

        <div className="flex items-start gap-3">
          <span className="mt-0.5 hidden h-5 border-l border-neutral-200 md:block" />
          <NonEditableFormField
            value={guestStatus}
            isCopy={false}
            label={t("reservation.form.guestStatus")}
            labelClassName="text-foreground"
            contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
            containerClassName="flex-1"
          />
        </div>

        <NonEditableFormField
          value={bookingNumber?.toString() || ""}
          isCopy={true}
          label={t("reservation.form.bookingNumber")}
          labelClassName="text-foreground"
          contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
          buttonClassName="!p-0"
        />

        <div className="flex items-start gap-3">
          <span className="hidden w-px shrink-0 md:block" aria-hidden />
          <div className="min-w-0 flex-1">
            <label className="text-foreground text-sm font-medium">
              {t("reservation.form.departure")}
            </label>
            <DatePicker
              minDate={
                new Date(
                  new Date(arrivalDate).setDate(
                    new Date(arrivalDate).getDate() + 1,
                  ),
                )
              }
              isEdit={isEditing}
              date={new Date(departureDate)}
              onDateChange={(date) => onDateChange("departure", date)}
              placeholder={t("reservation.placeholders.chooseDate")}
              className={`text-muted-foreground mt-3 h-9 min-h-9 w-full justify-start rounded-lg border px-3 py-1 text-sm focus:ring-0 ${errors.departureDate ? "border-red-500" : "border-neutral-200"}`}
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              formatString="dd.MM.yyyy"
              showIcon={false}
            />
          </div>
        </div>

        <div className="flex items-start gap-3">
          <span className="hidden w-px shrink-0 md:block" aria-hidden />
          <NonEditableFormField
            value={guestsCount.children}
            label={t("reservation.form.numberOfChildren")}
            isCopy={false}
            labelClassName="text-foreground"
            contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
            containerClassName="flex-1"
          />
        </div>

        <div className="flex items-start gap-3">
          <span className="hidden w-px shrink-0 md:block" aria-hidden />
          <NonEditableFormField
            value={roomType as string}
            isCopy={false}
            label={t("reservation.form.category")}
            labelClassName="text-foreground"
            contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
            containerClassName="flex-1"
          />
        </div>

        <div className="flex items-start gap-3">
          <span className="hidden w-px shrink-0 md:block" aria-hidden />
          <ReservationStatusSelect
            value={status as ReservationStatusType}
            onChange={(value) =>
              setValue("status", value as ReservationStatusType)
            }
            isEdit={isEditing}
          />
        </div>

        <NonEditableFormField
          value={bookingSource || ""}
          isCopy={false}
          label={t("reservation.form.bookingSource")}
          labelClassName="text-foreground"
          contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
        />

        <div className="flex items-start gap-3">
          <span className="hidden w-px shrink-0 md:block" aria-hidden />
          <div className="min-w-0 flex-1">
            <NonEditableFormField
              value={nights.toString()}
              label={t("reservation.form.numberOfNight")}
              isCopy={false}
              labelClassName="text-foreground"
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
            />
          </div>
        </div>

        <div className="flex items-start gap-3">
          <span className="hidden w-px shrink-0 md:block" aria-hidden />
          <NonEditableFormField
            value={getLanguageDisplayName(
              reservation?.preferredLanguage || "en",
            )}
            label={t("reservation.form.preferredLanguage")}
            isCopy={false}
            labelClassName="text-foreground"
            contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
            containerClassName="flex-1"
          />
        </div>

        <div className="flex items-start gap-3">
          <span className="hidden w-px shrink-0 md:block" aria-hidden />
          <NonEditableFormField
            value={reservation?.pmsType || ""}
            isCopy={false}
            label={t("reservation.form.PMSCategory")}
            labelClassName="text-foreground"
            contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
            containerClassName="flex-1"
          />
        </div>

        {showRoomServiceStatus ? (
          <div className="flex items-start gap-3">
            <span className="hidden w-px shrink-0 md:block" aria-hidden />
            <NonEditableFormField
              value={roomServiceStatus}
              isCopy={false}
              label={t("reservation.form.roomServiceStatus")}
              labelClassName="text-foreground"
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              containerClassName="flex-1"
            />
          </div>
        ) : null}
      </div>

      {/* Pre-check-in section */}
      <div className="mt-12 mb-8">
        <h2 className="text-violet mb-4 text-sm">
          {t("reservation.profile.preCheckIn")}
        </h2>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <PreCheckStatusSelect
            value={precheckinStatus || PreCheckStatus.NOT_DONE}
            onChange={(value: string) =>
              setValue("preCheckinStatus", value as PreCheckStatus)
            }
            isEdit={isEditing}
          />

          <div className="flex items-start gap-3">
            <span className="mt-0.5 hidden h-5 border-l border-neutral-200 md:block" />
            <div className="flex-1">
              {isEditing ? (
                <div>
                  <label className="text-foreground mb-3 block text-sm font-medium">
                    {t("reservation.form.guestsEnteredInPreCheckIn")}
                  </label>
                  <Select
                    value={preCheckinEnteredGuestsSelectValue}
                    onValueChange={(value) => {
                      if (value === "0" || value === "1") {
                        setValue("preCheckinEnteredGuests", Number(value));
                      }
                    }}
                  >
                    <SelectTrigger
                      className="focus-visible:border-ring w-full cursor-pointer justify-start rounded-lg border border-neutral-200 data-[size=default]:h-9"
                      showIcon={false}
                    >
                      <SelectValue
                        placeholder={t("reservation.placeholders.chooseYesNo")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">
                        <span className="text-muted-foreground">
                          {t("reservation.form.preCheckinEnteredGuestsYes")}
                        </span>
                      </SelectItem>
                      <SelectItem value="0">
                        <span className="text-muted-foreground">
                          {t("reservation.form.preCheckinEnteredGuestsNo")}
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {errors?.preCheckinEnteredGuests?.message ? (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.preCheckinEnteredGuests.message}
                    </p>
                  ) : null}
                </div>
              ) : (
                <NonEditableFormField
                  value={preCheckinEnteredGuestsReadLabel}
                  isCopy={false}
                  label={t("reservation.form.guestsEnteredInPreCheckIn")}
                  labelClassName="text-foreground"
                  contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
                />
              )}
            </div>
          </div>
        </div>

        <div className="mt-1.5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="text-foreground text-sm font-medium">
              {t("reservation.form.estimatedTimeOfArrival")}
            </label>
            <DatePicker
              isEdit={isEditing}
              date={new Date(estimatedTimeOfArrival)}
              onDateChange={(date) =>
                setValue("estimatedTimeOfArrival", date.toISOString() || "")
              }
              placeholder={t("reservation.placeholders.chooseDate")}
              className={`text-muted-foreground mt-3 h-9 min-h-9 w-full justify-start rounded-lg border px-3 py-1 text-sm focus:ring-0 ${errors.estimatedTimeOfArrival ? "border-red-500" : "border-neutral-200"}`}
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              formatString="dd.MM.yyyy - hh:mm a"
              showIcon={false}
            />
          </div>

          <div className="flex items-start gap-3">
            <span className="hidden w-px shrink-0 md:block" aria-hidden />
            <NonEditableFormField
              value={
                reservation?.preCheckinCompletedAt
                  ? format(
                      reservation.preCheckinCompletedAt,
                      "dd.MM.yyyy - hh:mm a",
                    )
                  : t("reservation.form.preCheckinNotEntered")
              }
              isCopy={false}
              label={t("reservation.form.whenGuestEnteredInPreCheckIn")}
              labelClassName="text-foreground"
              contentClassName="h-9 mt-3 px-3 py-1 items-center border border-neutral-200 rounded-lg text-muted-foreground text-sm"
              containerClassName="flex-1"
            />
          </div>
        </div>

        <div className="mt-1.5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <ReservationCheckinMethodSelect
            value={checkinMethod}
            onChange={(value: string) => setValue("checkinMethod", value)}
            isEdit={isEditing}
          />
        </div>

        <CustomQuestionAnswers
          answers={reservation?.customQuestionAnswers ?? []}
        />
      </div>
    </>
  );
};
